import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { PermissionsService } from "../../services/permissions/permissions.service";
import moment from "moment-timezone";
import { ReportService } from 'src/app/services/report/report.service';
import { InsightsFilterModalComponent } from '../insights-filter-modal/insights-filter-modal.component';
import { RequestsPulseService } from 'src/app/services/requests/requests-pulse.service';
@Component({
  selector: 'app-generate-report',
  templateUrl: './generate-report.component.pug',
  styleUrls: ['./generate-report.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class GenerateReportComponent implements OnInit {

  @Input() filter = 'All';
  @Input() start!: any;
  @Input() end!: any;
  @Output() closeEvent = new EventEmitter<string>();

  dateDropdownOptions = [
    {key: 'custom', type: 'option', value: $localize`Custom`},
    {key: 'all', type: 'option', value: $localize`All`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Days`},
    {key: 'last_7_days', type: 'option', value: $localize`Last 7 days`},
    {key: 'last_30_days', type: 'option', value: $localize`Last 30 days`},
    {key: 'last_60_days', type: 'option', value: $localize`Last 60 days`},
    {key: 'last_90_days', type: 'option', value: $localize`Last 90 days`},
    {key: 'last_365_days', type: 'option', value: $localize`Last 365 days`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar weeks`},
    {key: 'current_week', type: 'option', value: $localize`Current week`},
    {key: 'last_week', type: 'option', value: $localize`Last week`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar months`},
    {key: 'current_month', type: 'option', value: $localize`Current month`},
    {key: 'last_month', type: 'option', value: $localize`Last month`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar quarters`},
    {key: 'current_quarter', type: 'option', value: $localize`Current quarter`},
    {key: 'last_quarter', type: 'option', value: $localize`Last quarter`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar years`},
    {key: 'current_year', type: 'option', value: $localize`Current year`},
    {key: 'last_year', type: 'option', value: $localize`Last year`},
  ];
  loading = false;
  selectedDate = $localize`Last 30 days`;
  showDatesRow = false;
  dropdownPlaceholder = $localize`Select the report type`;
  fromDateLabel = $localize`Start Date`;
  toDateLabel = $localize`End Date`;
  showStartCalendar = false;
  showEndCalendar = false;
  startStr = '';
  endStr = '';
  reportTypeDropdownOptions = [
    {key: 'talent_pool', type: 'option', value: $localize`Talent pool`},
    {key: 'pulse_export', type: 'option', value: $localize`Pulse report`},
  ];
  selectedReportType: {key: string; type: string; value: string} | null = null;

  get disableGenerateBtn(): boolean {
    return !this.selectedReportType;
  }

  pulseStart = moment().subtract(1, 'years');
  pulseEnd = moment();
  pulseRequests: any[] = [];
  pulseSurveys: any[] = [];
  pulseDepartments: any[] = [];
  pulseRequestIds: number[] = [];
  pulseSurveyIds: number[] = [];
  pulseDepartmentIds: number[] = [];
  allowedPulseRequestIds = new Set<number>();
  allowedPulseDepartmentIds = new Set<number>();
  allowedPulseSurveyIds = new Set<number>();
  requestFilterPlaceholder = $localize`All requests`;
  surveyFilterPlaceholder = $localize`All surveys`;
  departmentFilterPlaceholder = $localize`All`;
  dateFilterPlaceholder = $localize`Last 365 days`;
  pulseDateFilter = $localize`Last 365 days`;
  pulseShowDatesRow = false;
  insightsFilterModalRef!: BsModalRef;

  get requestDisplayItems(): { name: string }[] {
    return this.pulseRequests.length
      ? this.pulseRequests.map(r => ({ name: r.name }))
      : [];
  }

  get surveyDisplayItems(): { name: string }[] {
    return this.pulseSurveys.length
      ? this.pulseSurveys.map(s => ({ name: s.name }))
      : [];
  }

  get departmentDisplayItems(): { name: string }[] {
    return this.pulseDepartments.length
      ? this.pulseDepartments.map(d => ({ name: d.department }))
      : [];
  }

  get isRequestDisabled(): boolean {
    return this.pulseSurveys.length > 0;
  }

  get isSurveyDisabled(): boolean {
    return this.pulseRequests.length > 0 || this.pulseDepartments.length > 0;
  }

  get isDepartmentDisabled(): boolean {
    return this.pulseSurveys.length > 0;
  }

  get isDateDisabled(): boolean {
    return this.pulseRequests.length > 0 || this.pulseDepartments.length > 0;
  }

  constructor(
    private modalService: BsModalService,
    private reportService: ReportService,
    private permissionsService: PermissionsService,
    private requestsPulseService: RequestsPulseService,
  ) { }

  ngOnInit(): void {
    this.initializeItemsBasedOnPermissions();
    this.calculateLastDays(30);
    if (this.selectedReportType?.key === 'pulse_export') {
      this.initializePulseFilters();
    }
  }

  initializeItemsBasedOnPermissions(): void {
    this.reportTypeDropdownOptions = this.reportTypeDropdownOptions.filter(item => {
      switch (item.key) {
        case 'pulse_export':
          return (this.permissionsService.hasPermission('can_generate_and_view_pulse_report') ||
                 this.permissionsService.hasPermission('can_generate_and_view_pulse_report_with_survey_perms'));
        case 'talent_pool':
          return this.permissionsService.hasPermission('can_view_or_generate_talent_pool_export_reports');
        default:
          return false;
      }
    });
  } 

  closeModal(reason): void {
    this.modalService.hide();
    this.closeEvent.emit(reason);
  }

  optInReportTypeDropdownChange(event: any) {
    this.selectedReportType = event;
    if (this.selectedReportType?.key === 'pulse_export') {
      this.initializePulseFilters();
    }
  }

  initializePulseFilters(): void {
    this.pulseStart = moment().subtract(1, 'years');
    this.pulseEnd = moment();
    this.pulseDateFilter = $localize`Last 365 days`;
    this.pulseShowDatesRow = false;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('insights-pulse-date-filter', this.pulseDateFilter);
    }
  }

  clearPulseFilters(): void {
    this.pulseRequests = [];
    this.pulseSurveys = [];
    this.pulseDepartments = [];
    this.pulseRequestIds = [];
    this.pulseSurveyIds = [];
    this.pulseDepartmentIds = [];
    this.allowedPulseRequestIds = new Set<number>();
    this.allowedPulseDepartmentIds = new Set<number>();
    this.allowedPulseSurveyIds = new Set<number>();
    this.requestFilterPlaceholder = $localize`All requests`;
    this.surveyFilterPlaceholder = $localize`All surveys`;
    this.departmentFilterPlaceholder = $localize`All`;
    this.pulseDateFilter = $localize`Last 365 days`;
    this.initializePulseFilters();
  }

  optInPulseDateDropdownChange(event: any): void {
    const value = event.value;
    this.pulseDateFilter = value;

    if (value === $localize`Custom`) {
      this.pulseShowDatesRow = true;
      this.pulseStart = moment().startOf('day').subtract(30, 'days');
      this.pulseEnd = moment();
    } else {
      this.pulseShowDatesRow = false;
    }

    if (event?.key === 'all') {
      this.pulseStart = moment('1970-01-01T00:00:00.000');
      this.pulseEnd = moment();
    }

    const daysMap: Record<string, number> = {
      [$localize`Last 7 days`]: 7,
      [$localize`Last 30 days`]: 30,
      [$localize`Last 60 days`]: 60,
      [$localize`Last 90 days`]: 90,
      [$localize`Last 365 days`]: 365,
    };
    if (value in daysMap) {
      this.pulseShowDatesRow = false;
      this.pulseStart = moment().startOf('day').subtract(daysMap[value], 'days');
      this.pulseEnd = moment();
    }

    const calendarFilters = [
      { label: $localize`Current week`, key: 'currentWeek' },
      { label: $localize`Last week`, key: 'lastWeek' },
      { label: $localize`Current month`, key: 'currentMonth' },
      { label: $localize`Last month`, key: 'lastMonth' },
      { label: $localize`Current quarter`, key: 'currentQuarter' },
      { label: $localize`Last quarter`, key: 'lastQuarter' },
      { label: $localize`Current year`, key: 'currentYear' },
      { label: $localize`Last year`, key: 'lastYear' },
    ];
    const selectedFilter = calendarFilters.find(f => f.label === value);
    if (selectedFilter) {
      this.pulseShowDatesRow = false;
      this.calculatePulseCalendarDates(selectedFilter.key);
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('insights-pulse-date-filter', this.pulseDateFilter);
    }
  }

  calculatePulseCalendarDates(filter: string): void {
    switch (filter) {
      case 'currentWeek':
        this.pulseStart = moment().startOf('week');
        this.pulseEnd = moment().endOf('week');
        break;
      case 'lastWeek':
        this.pulseStart = moment().subtract(1, 'weeks').startOf('week');
        this.pulseEnd = moment().subtract(1, 'weeks').endOf('week');
        break;
      case 'currentMonth':
        this.pulseStart = moment().startOf('month');
        this.pulseEnd = moment().endOf('month');
        break;
      case 'lastMonth':
        this.pulseStart = moment().subtract(1, 'months').startOf('month');
        this.pulseEnd = moment().subtract(1, 'months').endOf('month');
        break;
      case 'currentQuarter':
        this.pulseStart = moment().startOf('quarter');
        this.pulseEnd = moment().endOf('quarter');
        break;
      case 'lastQuarter':
        this.pulseStart = moment().subtract(1, 'quarters').startOf('quarter');
        this.pulseEnd = moment().subtract(1, 'quarters').endOf('quarter');
        break;
      case 'currentYear':
        this.pulseStart = moment().startOf('year');
        this.pulseEnd = moment().endOf('year');
        break;
      case 'lastYear':
        this.pulseStart = moment().subtract(1, 'years').startOf('year');
        this.pulseEnd = moment().subtract(1, 'years').endOf('year');
        break;
    }
  }

  onPulseDateChange(dates: { start: moment.Moment; end: moment.Moment }): void {
    this.pulseStart = dates.start;
    this.pulseEnd = dates.end;
  }

  onPulseCalendarVisibilityChange(event: { showStart: boolean; showEnd: boolean }): void {
  }

  openFilterModal(filterType: 'request' | 'survey' | 'department'): void {
    if (this.selectedReportType?.key !== 'pulse_export') return;
    if (filterType === 'request' && this.isRequestDisabled) return;
    if (filterType === 'survey' && this.isSurveyDisabled) return;
    if (filterType === 'department' && this.isDepartmentDisabled) return;

    {
      let initialState: Record<string, unknown> = {};
      if (filterType === 'request') {
        initialState = {
          filterType: 'request',
          selectedItems: [...this.pulseRequests],
          allowedItems: this.allowedPulseRequestIds,
          start: this.pulseStart,
          end: this.pulseEnd,
          searchOptions: [{ label: $localize`Request`, value: 'request' }, { label: $localize`Created by`, value: 'created_by' }],
          sortOptions: [
            { key: $localize`Request`, value: [{ label: $localize`A - Z`, sortString: 'request' }, { label: $localize`Z - A`, sortString: '-request' }] },
            { key: $localize`Created by`, value: [{ label: $localize`A - Z`, sortString: 'created_by' }, { label: $localize`Z - A`, sortString: '-created_by' }] },
            { key: $localize`Send date`, value: [{ label: $localize`Oldest`, sortString: 'send_date' }, { label: $localize`Newest`, sortString: '-send_date' }] },
            { key: $localize`Cutoff date`, value: [{ label: $localize`Oldest`, sortString: 'cutoff_date' }, { label: $localize`Newest`, sortString: '-cutoff_date' }] },
          ],
        };
      } else if (filterType === 'department') {
        initialState = {
          filterType: 'department',
          selectedItems: [...this.pulseDepartments],
          allowedItems: this.allowedPulseDepartmentIds,
          start: this.pulseStart,
          end: this.pulseEnd,
          selectedSurveyIds: this.pulseSurveyIds,
          searchOptions: [{ label: $localize`Department`, value: 'department' }, { label: $localize`Category`, value: 'category_name' }],
          sortOptions: [
            { key: $localize`Department`, value: [{ label: $localize`A - Z`, sortString: 'department' }, { label: $localize`Z - A`, sortString: '-department' }] },
            { key: 'group-end', value: [] },
            { key: $localize`Department category`, value: [{ label: $localize`A - Z`, sortString: 'category' }, { label: $localize`Z - A`, sortString: '-category' }] },
          ],
        };
      } else if (filterType === 'survey') {
        initialState = {
          filterType: 'survey',
          selectedItems: [...this.pulseSurveys],
          allowedItems: this.allowedPulseSurveyIds,
          start: this.pulseStart,
          end: this.pulseEnd,
          selectedDepartmentIds: this.pulseDepartmentIds,
          searchOptions: [{ label: $localize`Survey title`, value: 'title' }, { label: $localize`Created by`, value: 'name' }],
          sortOptions: [
            { key: $localize`Survey title`, value: [{ label: $localize`A - Z`, sortString: 'title' }, { label: $localize`Z - A`, sortString: '-title' }] },
            { key: $localize`Created by`, value: [{ label: $localize`A - Z`, sortString: 'name' }, { label: $localize`Z - A`, sortString: '-name' }] },
            { key: $localize`Created date`, value: [{ label: $localize`Oldest`, sortString: 'created' }, { label: $localize`Newest`, sortString: '-created' }] },
            { key: $localize`Last used`, value: [{ label: $localize`Oldest`, sortString: 'last_used' }, { label: $localize`Newest`, sortString: '-last_used' }] },
            { key: $localize`Times used`, value: [{ label: $localize`Ascending`, sortString: 'count' }, { label: $localize`Descending`, sortString: '-count' }] },
          ],
        };
      }
      this.insightsFilterModalRef = this.modalService.show(InsightsFilterModalComponent, {
        class: 'modal-dialog-centered xl-popup',
        initialState,
      });
      this.insightsFilterModalRef.content.applyFilter.subscribe((res: any[]) => {
        if (filterType === 'request') {
          this.pulseRequests = res;
          this.pulseRequestIds = res.map(i => i.id);
          this.setPulseDateFromRequests();
        } else if (filterType === 'department') {
          this.pulseDepartments = [...res];
          this.pulseDepartmentIds = res.map(i => i.id);
        } else if (filterType === 'survey') {
          this.pulseSurveys = res;
          this.pulseSurveyIds = res.map(i => i.questionnaire.id);
        }
      });
      this.insightsFilterModalRef.content.setLastSelected.subscribe((res: string) => {
        if (filterType === 'request') {
          this.requestFilterPlaceholder = res;
        } else if (filterType === 'department') {
          this.departmentFilterPlaceholder = res;
        } else if (filterType === 'survey') {
          this.surveyFilterPlaceholder = res;
        }
      });
      this.insightsFilterModalRef.content.setAllowedItems.subscribe((res: any[]) => {
        if (filterType === 'request') {
          this.allowedPulseDepartmentIds = new Set(
            res.map(a => a.department_id).filter((id): id is number => id != null)
          );
        } else if (filterType === 'department') {
          this.allowedPulseRequestIds = new Set(
            res.map(a => a.omni_check_id).filter((id): id is number => id != null)
          );
          this.allowedPulseSurveyIds = new Set(
            res.map(a => a.questionnaire_id).filter((id): id is number => id != null)
          );
        } else if (filterType === 'survey') {
          this.allowedPulseDepartmentIds = new Set(
            res.map(a => a.department_id).filter((id): id is number => id != null)
          );
        }
      });
    }
  }

  setPulseDateFromRequests(): void {
    if (this.pulseRequests.length) {
      const createdDates = this.pulseRequests.map(r => moment(r.created));
      const expiryDates = this.pulseRequests.map(r => moment(r.expiry || r.created));
      this.pulseStart = moment.min(createdDates);
      this.pulseEnd = moment.max(expiryDates);
    }
  }

  calculateLastDays(days: number) {
    this.showDatesRow = false;
    this.start = moment().startOf('day').subtract(days, 'days');
    this.end = moment();
  }


  calculateCalendarDates(filter: string) {
    this.showDatesRow = false;
    switch (filter) {
      case 'currentWeek':
        this.start = moment().startOf('week');
        this.end = moment().endOf('week');
        break;
      case 'lastWeek':
        this.start = moment().subtract(1, 'weeks').startOf('week');
        this.end = moment().subtract(1, 'weeks').endOf('week');
        break;
      case 'currentMonth':
        this.start = moment().startOf('month');
        this.end = moment().endOf('month');
        break;
      case 'lastMonth':
        this.start = moment().subtract(1, 'months').startOf('month');
        this.end = moment().subtract(1, 'months').endOf('month');
        break;
      case 'currentQuarter':
        this.start = moment().startOf('quarter');
        this.end = moment().endOf('quarter');
        break;
      case 'lastQuarter':
        this.start = moment().subtract(1, 'quarters').startOf('quarter');
        this.end = moment().subtract(1, 'quarters').endOf('quarter');
        break;
      case 'currentYear':
        this.start = moment().startOf('year');
        this.end = moment().endOf('year');
        break;
      case 'lastYear':
        this.start = moment().subtract(1, 'years').startOf('year');
        this.end = moment().subtract(1, 'years').endOf('year');
        break;
    }
  }


  optInDateDropdownChange(event: any) {
    const value = event.value;
    this.selectedDate = value;
    this.filter = value;

    if (value === $localize`Custom`) {
      this.showDatesRow = true;
      this.start = moment().startOf('day').subtract(30, 'days');
      this.end = moment();
    } else {
      this.showDatesRow = false;
    }

    if (event?.key === 'all') {
      this.start = moment('1970-01-01T00:00:00.000');
      this.end = moment();
    }

    // Match days filters
    const daysMap = {
      [$localize`Last 7 days`]: 7,
      [$localize`Last 30 days`]: 30,
      [$localize`Last 60 days`]: 60,
      [$localize`Last 90 days`]: 90,
      [$localize`Last 365 days`]: 365
    };

    if (value in daysMap) {
      this.calculateLastDays(daysMap[value]);
    }

    // Match calendar-based filters
    const calendarFilters = [
      { label: $localize`Current week`, key: 'currentWeek' },
      { label: $localize`Last week`, key: 'lastWeek' },
      { label: $localize`Current month`, key: 'currentMonth' },
      { label: $localize`Last month`, key: 'lastMonth' },
      { label: $localize`Current quarter`, key: 'currentQuarter' },
      { label: $localize`Last quarter`, key: 'lastQuarter' },
      { label: $localize`Current year`, key: 'currentYear' },
      { label: $localize`Last year`, key: 'lastYear' },
    ];

    const selectedFilter = calendarFilters.find(f => f.label === value);
    if (selectedFilter) {
      this.calculateCalendarDates(selectedFilter.key);
    }
  }

  onCalendarVisibilityChange(event: { showStart: boolean; showEnd: boolean }) {
    this.showStartCalendar = event.showStart;
    this.showEndCalendar = event.showEnd;
  }

  onDateChange(dates: { start: moment.Moment, end: moment.Moment }) {
    this.start = dates.start;
    this.end = dates.end;
  }

  generateReport(): void {
    if (!this.selectedReportType) return;
    const reportTypeKey = this.selectedReportType.key;
    const reportType = reportTypeKey;

    const isPulseReport = reportTypeKey === 'pulse_export';

    const fromDate = isPulseReport ? this.pulseStart : this.start;
    const toDate = isPulseReport ? this.pulseEnd : this.end;

    const data: Record<string, unknown> = {
      report_type: reportType,
      timezone: moment.tz.guess(),
    };

    if (isPulseReport) {
      data.from_date = moment(fromDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      data.to_date = moment(toDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      data.request_ids = this.isRequestDisabled ? [] : this.pulseRequestIds;
      data.survey_ids = this.isSurveyDisabled ? [] : this.pulseSurveyIds;
      data.department_ids = this.isDepartmentDisabled ? [] : this.pulseDepartmentIds;
    } else {
      data.from_date = moment(fromDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      data.to_date = moment(toDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    }
    this.loading = true;
    this.reportService.generateAndExportReport(data).subscribe({
      next: (data) => {
         this.loading = false;
         this.closeModal('generate-report-clicked');
      },
      error: (error) => {
        this.loading = false;
        console.error('Failed to generate report:', error);
        // Could emit error event or show error message
      }
    });
  }
}

